import { useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { I18N_KEYS } from "../languages/PlaceHolder";
import { useTranslation } from "react-i18next";
import { getFormEntriesByFormName } from "../services/formServices";
import { FORMNAMES } from "../utilities/codes";
import { MdKeyboardArrowDown } from "react-icons/md";
import { loaderContext } from "../contexts/LoaderProvider";
import toast from "react-hot-toast";
import { FaDownload, FaInbox } from "react-icons/fa";
import { APIENDPOINT } from "../services/apiHelper";

const MenuItems = ({ isPage = false, isMobile = false, setMenuOpen, schoolDetails }) => {
    const location = useLocation();
    const { setIsLoading } = useContext(loaderContext);
    const { i18n, t } = useTranslation();
    const [openSubmenu, setOpenSubmenu] = useState(null);
    const [page, setPage] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('i18n') || 'en')

    useEffect(() => {
        const getPages = async () => {
            try {
                setIsLoading(true);
                const fieldsToSelect = "payload.category,payload.title,payload.slug";
                const filter = { publish: { operator: "equals", value: true }, is_generic_page: { operator: "notEquals", value: true } };

                const pages = await getFormEntriesByFormName(
                    FORMNAMES.DYANAMICFORM,
                    filter,
                    { fields: fieldsToSelect, page: 1, limit: 100 }
                );

                setPage(pages);
            } catch (error) {
                toast.error(error?.message);
            } finally {
                setIsLoading(false);
            }
        };

        getPages();
    }, [setIsLoading]);

    const groupedObj = Object.groupBy(
        page ?? [],
        e => e?.payload?.category ?? ''
    );

    const groupedArray = Object.entries(groupedObj).map(
        ([category, items]) => ({
            category,
            items,
        })
    );
    // Menu array with submenu support
    const menuItems = useMemo(() => ([
        { name: t(I18N_KEYS.HOMEPAGENAME), path: "/" },
        { name: t(I18N_KEYS.ABOUTPAGENAME), path: "/about" },
        { name: t(I18N_KEYS.DIGNATARIESPAGENAME), path: "/dignitaries" },
        {
            name: t(I18N_KEYS.HONORS),
            path: "#",
            submenu: [
                { name: t(I18N_KEYS.TOPSTUDENTPAGENAME), path: "/topstudents" },
                { name: t(I18N_KEYS.TOPTEACHERPAGENAME), path: "/topteachers" }
            ]
        },
        { name: t(I18N_KEYS.GALERYPAGENAME), path: "/gallery" },
        { name: t(I18N_KEYS.CONTACTUSPAGENAME), path: "/contact" },
        { name: t(I18N_KEYS.NOTICEBOARDNAME), path: "/notice" },
        ...(page.length > 0
            ? [{
                name: t(I18N_KEYS.PAGES),
                path: "/pages",
                submenu: groupedArray.map(group => ({
                    name: group?.category,
                    path: "#",
                    submenu: group?.items?.map(item => ({
                        name: item?.payload?.title,
                        path: `/pages/${item?.payload?.slug}`
                    }))
                }))
            }]
            : [])
    ]), [t, page, groupedArray]);

    const isSubmenuActive = (item) => {
        if (item?.path === "/pages" && location?.pathname.includes("/pages/")) {
            const skipActiveSubPages = ["/pages/privacy", "/pages/terms"]
            if (skipActiveSubPages.some(e => e === location?.pathname)) {
                return false;
            }
            return true;
        }
        if (item.submenu) {
            return item?.submenu?.some(subItem => location?.pathname === subItem?.path);
        }
        return false;
    };

    const linkClass = ({ isActive }) =>
        isActive
            ? "text-secondary font-medium transition-all"
            : "text-primary hover:text-secondary font-medium transition-all";

    const downloadBrochure = async () => {
        try {
            const fileUrl = APIENDPOINT + schoolDetails?.brochure?.fileUrl;

            const response = await fetch(fileUrl);
            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `${schoolDetails?.name}.pdf`;
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
        }
    };
    const languages = [
        { title: "English", value: "en" },
        { title: "हिन्दी", value: "hi" },
        { title: "ગુજરાતી", value: "gu" }
    ]

    const setLanguage = (e) => {
        localStorage.setItem('i18n', e?.target?.value);
        i18n?.changeLanguage(e?.target?.value);
        setSelectedLanguage(e?.target?.value);
    }
    return (<>
        {isPage ? (<>
            {groupedArray.length > 0 ? <div className="common-container py-3 z-50 min-h-[44vh]">
                {/* full page */}
                <div
                    className=" grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-3 gap-y-6 gap-x-6 mt-4 ml-0 lg:ml-64 px-4 sm:px-6 lg:px-0"
                >
                    {groupedArray.map((subItem, subIndex) => (
                        <div key={subIndex}>
                            <h4 className="text-base font-semibold text-primary mb-3 capitalize">
                                {subItem?.category?.toLowerCase()}
                            </h4>

                            <ul className="space-y-3">
                                {subItem?.items?.map((sItem, sIndex) => (
                                    <li key={sIndex}>
                                        <NavLink
                                            to={`/pages/${sItem?.payload?.slug}`}
                                            onClick={() => setOpenSubmenu(null)}
                                            className="  capitalize block text-sm sm:text-base text-gray-700 hover:text-secondary transition "
                                        >
                                            {sItem?.payload?.title?.toLowerCase()}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div> : <section className="p-32 flex items-center justify-center from-slate-50 via-blue-50 to-indigo-50 px-6">
                <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="flex items-center justify-center w-24 h-24 rounded-full bg-blue-100 text-blue-600 shadow-md animate-bounce mb-4">
                        <FaInbox className="text-5xl text-primary" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-semibold text-primary">
                        {t(I18N_KEYS.NOPAGETITLE)}
                    </h2>
                    <p className="text-gray-700 text-center text-base md:text-lg max-w-2xl mx-auto">
                        {t(I18N_KEYS.NOPAGEDES)}
                    </p>
                    <Link
                        to="/"
                        className="bg-primary text-accent px-8 py-4 rounded-full text-lg font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform"
                    >
                        {t(I18N_KEYS.GOTO)} {t(I18N_KEYS.HOMEPAGENAME)}
                    </Link>
                </div>
            </section>}
        </>) :
            // DeskTop View
            (<>{!isMobile && menuItems.map((item, index) => (
                <div
                    key={index}
                    className="relative"
                >
                    {item?.submenu ? (
                        <div
                            className="relative group"
                            onMouseEnter={() => setOpenSubmenu(index)}
                            onMouseLeave={() => setOpenSubmenu(null)}
                        >
                            <button className={`${linkClass({ isActive: isSubmenuActive(item) })} flex items-center gap-1.5 cursor-pointer`}>
                                {item?.name}
                                <MdKeyboardArrowDown className={`text-lg transition-transform duration-200 ${openSubmenu === index ? 'rotate-180' : ''}`} />
                            </button>
                            {openSubmenu === index && (
                                <>
                                    {item?.submenu.filter(subItem => subItem?.submenu).length > 0 && (
                                        <div className="absolute top-full right-0 w-[630px] bg-white rounded-lg shadow-2xl border border-gray-200 py-3 z-50">
                                            <div className="grid grid-cols-3 gap-y-3 px-5 m-4">
                                                {item?.submenu.map((subItem, subIndex) => (
                                                    <div key={subIndex}>
                                                        <h4 className="text-md font-semibold text-primary mb-2 capitalize"  >
                                                            {subItem?.name?.toLowerCase()}
                                                        </h4>

                                                        <ul className="space-y-2">
                                                            {subItem?.submenu.map((sItem, sIndex) => (
                                                                <li key={sIndex}>
                                                                    <NavLink
                                                                        to={sItem?.path}
                                                                        onClick={() => setOpenSubmenu(null)}
                                                                        className={({ isActive }) =>
                                                                            `capitalize block text-sm text-gray-600 hover:text-secondary hover:underline transition ${isActive ? 'bg-primary/10 font-semibold text-secondary' : ''
                                                                            }`
                                                                        }
                                                                    >
                                                                        {sItem?.name?.toLowerCase()}
                                                                    </NavLink>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {item?.submenu.filter(subItem => !subItem?.submenu).length > 0 && (
                                        <div className="absolute top-full left-0 w-56 bg-white rounded-lg shadow-2xl border border-gray-200 py-3 z-50">
                                            {item?.submenu?.map((subItem, subIndex) => (
                                                <NavLink
                                                    key={subIndex}
                                                    to={subItem?.path}
                                                    onClick={() => setOpenSubmenu(null)}
                                                    className={({ isActive }) =>
                                                        `capitalize block px-5 py-3 text-primary hover:bg-primary hover:text-accent transition-all ${isActive ? 'bg-primary/10 font-semibold text-secondary' : ''
                                                        }`
                                                    }
                                                >
                                                    {subItem?.name?.toLowerCase()}
                                                </NavLink>
                                            ))}
                                        </div>
                                    )}

                                </>
                            )}


                        </div>
                    ) : (
                        <NavLink to={item?.path} className={linkClass}>
                            {item?.name}
                        </NavLink>
                    )}
                </div>
            ))}
                {/* Mobile View */}
                {isMobile && <nav className="flex flex-col py-2">
                    {menuItems?.map((item, index) => (
                        <div key={index}>
                            {item.submenu ? (
                                <>
                                    <button
                                        onClick={() => setOpenSubmenu(openSubmenu === index ? null : index)}
                                        className={`w-full px-6 py-4 text-left transition-all flex items-center justify-between capitalize ${isSubmenuActive(item)
                                            ? 'bg-primary/10 text-primary font-semibold'
                                            : 'text-gray-800 hover:bg-primary/10 hover:text-primary'
                                            } ${openSubmenu === index ? 'bg-primary/10 text-primary' : ''
                                            }`}
                                    >
                                        <span className="font-semibold text-base">{item?.name?.toLowerCase()}</span>
                                        <MdKeyboardArrowDown
                                            className={`text-xl transition-transform duration-200 ${openSubmenu === index ? 'rotate-180' : ''
                                                }`}
                                        />
                                    </button>
                                    {openSubmenu === index && (
                                        <div className="bg-gray-50/50">
                                            {item?.submenu?.filter(subItem => subItem?.submenu?.length > 0)
                                                .map((subItem, subIndex) => (
                                                    <div key={subIndex} className="px-6 py-3 border-b border-gray-200 last:border-b-0">

                                                        <h4 className="text-sm font-semibold text-primary mb-2 capitalize">
                                                            {subItem?.name?.toLowerCase()}
                                                        </h4>

                                                        <div className="flex flex-col space-y-1">
                                                            {subItem?.submenu?.map((sItem, sIndex) => (
                                                                <NavLink
                                                                    key={sIndex}
                                                                    to={sItem?.path}
                                                                    onClick={() => {
                                                                        setMenuOpen(false);
                                                                        setOpenSubmenu(null);
                                                                    }}
                                                                    className={({ isActive }) =>
                                                                        `capitalize block px-4 py-2 rounded-md transition-all text-sm ${isActive
                                                                            ? "bg-primary/15 text-primary font-semibold"
                                                                            : "text-gray-700 hover:bg-primary/10 hover:text-primary"
                                                                        }`
                                                                    }
                                                                >
                                                                    {sItem?.name?.toLowerCase()}
                                                                </NavLink>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            {item.submenu.every(subItem => !subItem?.submenu?.length) && (
                                                <div className="bg-gray-50/50">
                                                    {item.submenu.map((subItem, subIndex) => (
                                                        <NavLink
                                                            key={subIndex}
                                                            to={subItem.path}
                                                            onClick={() => {
                                                                setMenuOpen(false);
                                                                setOpenSubmenu(null);
                                                            }}
                                                            className={({ isActive }) =>
                                                                `block px-8 py-3 transition-all capitalize ${isActive
                                                                    ? "bg-primary/15 text-primary font-semibold"
                                                                    : "text-gray-700 hover:bg-primary/10 hover:text-primary"
                                                                }`
                                                            }
                                                        >
                                                            {subItem?.name?.toLowerCase()}
                                                        </NavLink>
                                                    ))}
                                                </div>
                                            )}

                                        </div>
                                    )}
                                </>
                            ) : (
                                <NavLink
                                    to={item?.path}
                                    onClick={() => setMenuOpen(false)}
                                    className={({ isActive }) =>
                                        `block px-6 py-4 text-gray-800 hover:bg-primary/10 hover:text-primary transition-all font-medium ${isActive ? 'bg-primary/10 text-primary font-semibold' : ''
                                        }`
                                    }
                                >
                                    {item?.name}
                                </NavLink>
                            )}
                        </div>
                    ))}
                </nav>}

                {schoolDetails?.brochure &&
                    <button
                        className={isMobile ? "w-full px-6 py-4 text-left text-primary hover:bg-primary/10 transition-all font-semibold flex items-center gap-2" : "px-4 py-2 border-2 border-primary text-primary font-semibold rounded hover:bg-primary hover:text-accent transition-colors"}
                        onClick={() => {
                            downloadBrochure()
                            isMobile && setMenuOpen(false);
                        }}
                    >
                        {isMobile && <FaDownload />}
                        {t(I18N_KEYS.DOWNLOAD)}
                    </button>
                }
                {schoolDetails?.enable_multilanguage &&
                    <select
                        onChange={(e) => { setLanguage(e) }}
                        value={selectedLanguage}
                        className={isMobile ? "w-50 mt-2 ml-4 p-3 rounded border border-primary font-medium text-sm cursor-pointer transition" : "p-2 pl-3 pr-6 rounded border border-primary font-medium text-sm cursor-pointer transition"}
                    >
                        {languages?.map((lang, idx) => (
                            <option key={idx} value={lang?.value}>{lang?.title}</option>
                        ))}
                    </select>
                }
            </>
            )
        }

    </>)
}

export default MenuItems;